import {
  all,
  call,
  fork,
  takeEvery,
  takeLatest,
} from '@redux-saga/core/effects';
import { createReducer, createSelector } from '@reduxjs/toolkit';
import { COMMENT_INCLUDE } from '../../constants/api';
import {
  getAllCommentsForHearingInitialize,
  getAllCommentsForHearingSuccess,
  getAllHearingsSuccess,
  getPaginatedCommentsForHearingInitialize,
  getPaginatedCommentsForHearingSuccess,
  getPaginatedHearingsSuccess,
  removeCommentsFromStore,
  updateCommentSuccess,
} from '../actions/api';
import { CommentConnector } from '../connectors/commentConnector';
import { getAttr, getEntityId, getRelId } from '../helpers/apiHelpers';
import { removeItemFromList, updateDtoList } from '../helpers/reducerHelpers';
import { generateGetAllApiCall } from '../helpers/sagaHelpers';
import {
  getIncludedData,
  getRequestData,
  selectAllFromApiState,
} from '../helpers/selectorHelpers';
import { commentTypeByTypeSelector } from './commentTypeSlice';

// -------- Overview --------

// The following endpoints exist
//  - GET /hearing/{hearingId}/comment
//  - POST /hearing/{hearingId}/comment
//  - PATCH /hearing/{hearingId}/comment/{id}

// -------- Reducer --------
const commentReducer = createReducer(
  {
    all: [],
  },
  (builder) => {
    builder
      .addCase(getAllCommentsForHearingSuccess, (state, action) => {
        const comments = getRequestData(action);
        const includedComments = getIncludedData(action);
        const commentChildren = includedComments.filter(
          (include) => include.type === COMMENT_INCLUDE,
        );

        return updateDtoList(state, 'all', comments.concat(commentChildren));
      })
      .addCase(updateCommentSuccess, (state, action) => {
        const { commentId } = action.payload.initAction.payload;
        return removeItemFromList(state, 'all', commentId);
      })
      .addCase(removeCommentsFromStore, (state, action) => {
        const commentIds = action.payload;
        return removeItemFromList(state, 'all', commentIds);
      })
      .addCase(getAllHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const comments = data.filter(
          (include) => include.type === COMMENT_INCLUDE,
        );
        return updateDtoList(state, 'all', comments);
      })
      .addCase(getPaginatedHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const comments = data.filter(
          (include) => include.type === COMMENT_INCLUDE,
        );
        return updateDtoList(state, 'all', comments);
      })
      .addCase(getPaginatedCommentsForHearingSuccess, (state, action) => {
        const comments = getRequestData(action);
        const includedComments = getIncludedData(action);
        const commentChildren = includedComments.filter(
          (include) => include.type === COMMENT_INCLUDE,
        );

        return updateDtoList(state, 'all', comments.concat(commentChildren));
      });
  },
);

// --------- Sagas ----------
export function* commentSaga() {
  yield all([
    fork(getCommentsForHearing),
    fork(getPaginatedCommentsForHearing),
  ]);
}

function* getCommentsForHearing() {
  yield takeEvery(
    getAllCommentsForHearingInitialize.type,
    generateGetAllApiCall(CommentConnector, function* (connector, action) {
      return yield call(
        [connector, connector.getComments],
        action.payload.hearingId,
        action.payload.includes,
      );
    }),
  );
}

function* getPaginatedCommentsForHearing() {
  yield takeLatest(
    getPaginatedCommentsForHearingInitialize.type,
    generateGetAllApiCall(CommentConnector, function* (connector, action) {
      return yield call(
        [connector, connector.getPaginatedComments],
        action.payload.hearingId,
        action.payload.pageIndex,
        action.payload.pageSize,
        action.payload.includes,
      );
    }),
  );
}

// -------- Selectors --------
const selectGetAllComments = (state) => selectAllFromApiState(state, 'comment');
const allCommentsSelector = createSelector(
  selectGetAllComments,
  (comments) => comments,
);

const commentByIdSelector = (commentId) =>
  createSelector(allCommentsSelector, (comments) => {
    return comments.find((comment) => getEntityId(comment) === commentId);
  });

const allCommentsByCommentTypeSelector = (type) =>
  createSelector(
    [allCommentsSelector, commentTypeByTypeSelector(type)],
    (comments, commentType) =>
      comments.filter(
        (comment) =>
          getRelId(comment, 'commentType') === getEntityId(commentType),
      ),
  );

const allCommentsByHearingIdSelector = (hearingId) =>
  createSelector(selectGetAllComments, (comments) =>
    comments.filter((comment) => getRelId(comment, 'hearing') === hearingId),
  );

const allNotDeletedCommentsByHearingIdSelector = (hearingId) =>
  createSelector(allCommentsByHearingIdSelector(hearingId), (comments) =>
    comments.filter((comment) => !getAttr(comment, 'isDeleted')),
  );

const allNotDeletedCommentsWithTypeByHearingIdSelector = (
  hearingId,
  commentTypeId,
) =>
  createSelector(
    allNotDeletedCommentsByHearingIdSelector(hearingId),
    (comments) =>
      comments.filter(
        (comment) => getRelId(comment, 'commentType') === commentTypeId,
      ),
  );

// -------- Exporting --------
export {
  allCommentsSelector,
  commentByIdSelector,
  allCommentsByCommentTypeSelector,
  allCommentsByHearingIdSelector,
  allNotDeletedCommentsByHearingIdSelector,
  allNotDeletedCommentsWithTypeByHearingIdSelector,
};
export default commentReducer;
